import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC, useMemo } from 'react';
import theme from 'src/assets/theme/theme';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import {
	ANTRAGS_FORMULAREN,
	FINANZIERUNG_PLAN_ERROR_MELDUNG,
	KOSTEN_POSITION,
	KOSTENTRAEGER,
	PLATZHALTER_TEXT,
	VORHABENS_ART,
} from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { Required, ValidDate } from 'src/lib/validation/ValidationTypes';
import { isUnternehmenHochschule } from 'src/pages/private/online-antrag/formulas/foerderantrag/LoeweAntragFormulas';
import { getAuFPruefAssistenzFormStruc, PruefAssistenzAUF } from 'src/utils/ausgabenFinanzierungHelper';
import { DisplayDecimal, DisplayEuro } from 'src/utils/formats';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';
import { checkIfGroupsHaveNegativeValues, checkIfTotalsHaveNegativeValues } from '../../../../application-helper-function/AppHelperfunctions';

// import schemas
import Schema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenpositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
const P = Schema.attributes;
const KP_P = KostenpositionSchema.attributes;

const groupPropertiesToCheck: string[] = ['gesamtAusgabe', 'gesamtEinnahme', 'averageFoerderquote'];
const totalsPropertiesToCheck: string[] = ['gesamtGesamtausgaben', 'gesamtSummeEinnahme', 'gesamtSummeZuwendung', 'gesamtDivisionZuwendungEinnahme'];

const DEFAULT_MAX_FOERDERQUOTE = 100;
const VERBUNDPARTNER_HOCHSCHULE_MAX_FOERDERQUOTE = 90;
const VERBUNDPARTNER_UNTERNEHMEN_MAX_FOERDERQUOTE = 50;
const ANTRAGSTELLER_UNTERNEHMEN_MAX_FOERDERQUOTE = 50;

const getMaxFoerderquoteForGroup = (group: any): { maxFoerderquote: number; warningMessage: string } => {
	const companyName = group.companyName || 'Unbekanntes Unternehmen';
	let maxFoerderquote = DEFAULT_MAX_FOERDERQUOTE;
	let maxWarningMessage = '';

	if (group.kostenTraeger === KOSTENTRAEGER.Verbundpartner) {
		if (group.hochschule) {
			maxFoerderquote = VERBUNDPARTNER_HOCHSCHULE_MAX_FOERDERQUOTE;
			maxWarningMessage = `**${companyName}:** Bitte kontrollieren Sie die Höhe Ihrer Eigen- und Drittmittel. Ihre individuelle Förderquote darf höchstens ${DisplayDecimal(maxFoerderquote)}% betragen.`;
		} else {
			maxFoerderquote = VERBUNDPARTNER_UNTERNEHMEN_MAX_FOERDERQUOTE;
			maxWarningMessage = `**${companyName}:** Bitte kontrollieren Sie die Höhe Ihrer Eigenmittel. Ihre individuelle Förderquote darf höchstens ${DisplayDecimal(maxFoerderquote)}% betragen.`;
		}
	} else if (group.kostenTraeger === KOSTENTRAEGER.Antragsteller) {
		if (group.hochschule) {
			maxFoerderquote = DEFAULT_MAX_FOERDERQUOTE;
		} else {
			maxFoerderquote = ANTRAGSTELLER_UNTERNEHMEN_MAX_FOERDERQUOTE;
			maxWarningMessage = `**${companyName}:** Bitte kontrollieren Sie die Höhe Ihrer Eigenmittel. Ihre individuelle Förderquote darf höchstens ${DisplayDecimal(maxFoerderquote)}% betragen.`;
		}
	}

	return {
		maxFoerderquote,
		warningMessage: maxWarningMessage,
	};
};

const FinanzierungPlanSkizzeGlobal: FC<{}> = () => {
	const { application } = useApplication();

	const allKostenPosition = useMemo(() => {
		return get(application, P.Foerderantrag_Kostenpositions.path);
	}, [application]);
	const { groupedData, totals } = transformDataSkizze(allKostenPosition, application);
	const hasNegativeValues =
		checkIfGroupsHaveNegativeValues(groupedData, groupPropertiesToCheck) || checkIfTotalsHaveNegativeValues(totals, totalsPropertiesToCheck);

	const antragsformular = get(application, P.Allgemein_Antragsformular.path);
	const hasFoerderquoteMaxViolation = useMemo(() => {
		if (!(antragsformular === ANTRAGS_FORMULAREN.Loewe)) {
			return [];
		}

		return Object.values(groupedData).reduce((warnings: string[], group: any) => {
			const foerderquote = parseFloat(group.averageFoerderquote) || 0;
			const { maxFoerderquote, warningMessage } = getMaxFoerderquoteForGroup(group);
			if (foerderquote > maxFoerderquote) {
				warnings.push(warningMessage);
			}

			return warnings;
		}, []);
	}, [antragsformular, groupedData]);

	return (
		<>
			<ApplicationHeading title='Ausgaben- und Finanzierungsplan' description={PLATZHALTER_TEXT} />
			{hasNegativeValues ?
				<>
					<MUIAlert infoType={'error'} title={'Warnung'} message={FINANZIERUNG_PLAN_ERROR_MELDUNG} />
				</>
			:	null}
			{hasFoerderquoteMaxViolation.length > 0 ?
				hasFoerderquoteMaxViolation.map((warningText: string, index: number) => (
					<MUIAlert key={`foerderquote-warning-${index}`} infoType={'warning'} title={'Achtung:'} message={warningText} />
				))
			:	null}
			<TableContainer>
				<Table size='medium' aria-label='a dense table'>
					<TableHead>
						<TableRow>
							<TableCell></TableCell>
							<TableCell align='right'>Gesamt</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{Object.values(groupedData).map((group: any, index: number) => (
							<React.Fragment key={index}>
								<TableRow>
									<TableCell sx={{ backgroundColor: theme.palette.background.paper, fontWeight: 600 }} colSpan={2}>
										{group.companyName}
									</TableCell>
								</TableRow>
								<TableRow sx={{ '& td, & th': { p: '0.8rem 1rem' } }}>
									<TableCell>Ausgaben</TableCell>
									<TableCell
										align='right'
										style={{
											color: parseFloat(group.gesamtAusgabe) < 0 ? 'red' : 'inherit',
										}}
									>
										{DisplayEuro(group.gesamtAusgabe)}
									</TableCell>
								</TableRow>
								<TableRow sx={{ '& td, & th': { p: '0.8rem 1rem' } }}>
									<TableCell>Eigen- und Drittmittel</TableCell>
									<TableCell
										align='right'
										style={{
											color: parseFloat(group.gesamtEinnahme) < 0 ? 'red' : 'inherit',
										}}
									>
										{DisplayEuro(group.gesamtEinnahme)}
									</TableCell>
								</TableRow>
								{get(application, P.Allgemein_Vorhabensart.path) === VORHABENS_ART.Verbundvorhaben && (
									<TableRow sx={{ '& td, & th': { p: '0.8rem 1rem' } }}>
										<TableCell>Individuelle Förderquote</TableCell>
										<TableCell
											align='right'
											style={{
												color: parseFloat(group.averageFoerderquote) < 0 ? 'red' : 'inherit',
											}}
										>
											{DisplayDecimal(!isNaN(group.averageFoerderquote) ? group.averageFoerderquote : 0)}%
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						))}
						<TableRow className='sub-total-row'>
							<TableCell sx={{ fontWeight: 'bold' }}>Gesamtausgaben</TableCell>
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtGesamtausgaben) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtGesamtausgaben)}
							</TableCell>
						</TableRow>
						<TableRow className='sub-total-row'>
							<TableCell sx={{ fontWeight: 'bold' }}>Summe Eigen- und Drittmittel</TableCell>
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtSummeEinnahme) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtSummeEinnahme)}
							</TableCell>
						</TableRow>
						<TableRow className='total-row'>
							<TableCell>Beantragte Förderung</TableCell>
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtSummeZuwendung) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayEuro(totals.gesamtSummeZuwendung)}
							</TableCell>
						</TableRow>
						<TableRow className='total-row'>
							<TableCell>Beantragte Förderquote</TableCell>
							<TableCell
								align='right'
								style={{
									color: parseFloat(totals.gesamtDivisionZuwendungEinnahme) < 0 ? 'red' : 'inherit',
								}}
							>
								{DisplayDecimal(totals.gesamtDivisionZuwendungEinnahme > 0 ? totals.gesamtDivisionZuwendungEinnahme : 0)}%
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
			<PruefAssistenzAUF />
		</>
	);
};
export default FinanzierungPlanSkizzeGlobal;

//application only needed to set Hochschule or Unternehmen
export function transformDataSkizze(kostenPostitions: any, application?: any) {
	const groupedData: any = {};
	const allKostenPositionen = kostenPostitions || [];

	// Group by Unternehmen.id and calculate the totals directly from Allgemein_Geplante_Gesamtsumme
	allKostenPositionen.forEach((eachKP: any) => {
		const companyId = get(eachKP, `${KP_P.Unternehmen.path}.id`);
		const shouldSetHochschule = Boolean(application);
		const isUnternehmen = shouldSetHochschule ? isUnternehmenHochschule(eachKP, application) : undefined;
		const companyName =
			shouldSetHochschule && isUnternehmen ?
				get(eachKP, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Kuerzel.path, '') ||
				get(eachKP, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '')
			:	get(eachKP, KP_P.Unternehmen.schema.attributes.Geschaeftsbezogene_Daten_Name_Der_Organisation.path, '');
		const kostenTraeger = get(eachKP, KP_P.Allgemein_Kostentraeger.path, '');

		if (!groupedData[companyId]) {
			groupedData[companyId] = {
				companyName,
				...(shouldSetHochschule ? { hochschule: Boolean(isUnternehmen) } : {}),
				...(shouldSetHochschule ? { kostenTraeger: kostenTraeger } : {}),
				gesamtAusgabe: 0,
				gesamtEinnahme: 0,
			};
		}

		const plannedSum = parseFloat(get(eachKP, KP_P.Allgemein_Geplante_Gesamtsumme.path, 0)) || 0;

		if (get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe) {
			groupedData[companyId].gesamtAusgabe += plannedSum;
		} else if (get(eachKP, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme) {
			groupedData[companyId].gesamtEinnahme += plannedSum;
		}
	});

	// Calculate individuelle Förderquote
	Object.values(groupedData).forEach((group: any) => {
		group.averageFoerderquote = (((group.gesamtAusgabe - group.gesamtEinnahme) / group.gesamtAusgabe) * 100).toFixed(2);
	});

	// Calculate overall totals
	const totals: any = {
		gesamtGesamtausgaben: 0,
		gesamtSummeEinnahme: 0,
		gesamtSummeZuwendung: 0,
		gesamtDivisionZuwendungEinnahme: 0,
	};

	Object.values(groupedData).forEach((group: any) => {
		totals.gesamtGesamtausgaben += group.gesamtAusgabe;
		totals.gesamtSummeEinnahme += group.gesamtEinnahme;
	});

	totals.gesamtSummeZuwendung = (totals.gesamtGesamtausgaben - totals.gesamtSummeEinnahme).toFixed(2);

	totals.gesamtDivisionZuwendungEinnahme = ((parseFloat(totals.gesamtSummeZuwendung) / totals.gesamtGesamtausgaben) * 100).toFixed(2);

	return { groupedData, totals };
}

const formStrucAlwaysFalse = () => {
	return [
		{
			fields: [
				{
					...P.Allgemein_Projektdauerin_Jahren,
					validation: [{ validateFunction: Required }, { validateFunction: ValidDate }],
				},
			],
		},
	];
};
export const getFormStrucFinanzierungPlanSkizzeGlobal = (application: any) => {
	let result: FormStrucItem[] = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path);

	const { groupedData, totals } = transformDataSkizze(allKostenPosition);
	const hasNegativeValues =
		checkIfGroupsHaveNegativeValues(groupedData, groupPropertiesToCheck) || checkIfTotalsHaveNegativeValues(totals, totalsPropertiesToCheck);

	if (hasNegativeValues) {
		result = concat(result, formStrucAlwaysFalse());
	}
	result = getAuFPruefAssistenzFormStruc(application, result);
	return result;
};
